<template>
  <v-container fluid id="inspections-page">
    <v-row class="pa-sm-4 pa-md-6 pa-xl-8">
      <v-col cols="12" sm="12" md="6">
        <v-card flat class="my-card" id="inspections-card" min-height="520">
          <v-row>
            <v-col cols="12" class="d-inline-flex">
              <div class="mr-2">
                <v-img
                  contain
                  height="24"
                  width="24"
                  src="@/assets/microphone.svg"
                ></v-img>
              </div>
              <h2>{{ $t('inspections') }}</h2>
            </v-col>
          </v-row>

          <v-data-table
            id="audio-inspections-table"
            :headers="headers"
            :items="audioInspections"
            hide-default-header
            hide-default-footer
            :mobile-breakpoint="0"
            height="370px"
          >
            <template v-slot:item.checkbox="{ item }">
              <v-icon size="20">
                {{checkbox_off}}
              </v-icon>
            </template>
            <template v-slot:item.title="{ item }">
              <div @click.stop="selectAudioInspectionItem(item)"
                class="item-link"
                :class="{'active-task': item.active}">
                <div class="task-title">{{item.title}}</div>
                <div class="task-item-date d-flex" v-if="!item.active">
                  <span class="tasks-calendar-icon">
                    <v-img
                      contain
                      height="18"
                      width="18"
                      :src="`${publicPath}img/icons/`+item.icon+'.svg'"
                    ></v-img>
                  </span>
                  <span>{{item.formattedDate}}</span>
                  <span
                    v-if="(item.notes != '' && item.notes != null)"
                    class="file-icon"
                  >
                    <v-img
                      contain
                      height="18"
                      width="18"
                      src="@/assets/file.svg"
                    ></v-img>
                  </span>
                </div>
              </div>
            </template>
            <template v-slot:item.download="{ item }">
              <div @click.stop="downloadAudioInspection(item)">
                <v-btn icon>
                  <v-icon size="20">{{download_icon}}</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:item.delete="{ item }">
              <div @click.stop="onDeleteAudioInspection(item)">
                <v-btn icon>
                  <v-icon size="20">{{delete_icon}}</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:item.date="{ item }">
              <!-- Keep empty for not showing date on table -->
            </template>
          </v-data-table>

          <v-row class="mt-3">
            <v-col cols="12" class="pb-0">
              <v-btn
                class="my-btn add-inspection-btn"
                depressed
                block
                @click="onAddInspection"
              >
                <v-icon>mdi-plus</v-icon>
                <v-spacer></v-spacer>
                {{$t("addInspection")}}
                <v-spacer></v-spacer>
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="6" v-if="!isChosenAudioInspectionEmpty" class="hidden-sm-and-down">
        <AudioInspectionDetailsCard @edit-inspection="openEditAudioInspectionModal" />
      </v-col>

      <AudioInspectionsModal
        :visible="!isChosenAudioInspectionEmpty"
        class="hidden-md-and-up"
        @close="closeAudioInspectionsModal"
      />

      <EditAudioInspectionModal
        :visible="showEditAudioInspectionModal"
        @close="closeEditAudioInspectionModal"
        @save="updateAudioInspection"
      />

      <NewAudioInspectionModal
        :visible="showNewAudioInspectionModal"
        @save="saveNewInspection"
        @close="closeNewAudioInspectionModal"
      />

      <v-overlay :value="showLoader" z-index="10">
        <v-progress-circular
          indeterminate
          size="64"
        ></v-progress-circular>
      </v-overlay>
      
      <v-dialog
        v-model="showDeleteConfirmation"
        max-width="400"
      >
        <v-card class="my-card">
          <v-row>
            <v-col class="d-flex align-center">
              <div class="pr-2">
                <v-img
                  contain
                  height="24"
                  width="24"
                  class=""
                  src="@/assets/alert_icon.svg"
                ></v-img>
              </div>
              <div>
                <div class="warning-title">{{$t("warning")}}</div>
              </div>
            </v-col>
          </v-row>

          <v-card-text>
            <div class="warning-content">
              {{ $t('confirmInspectionDelete') }}
            </div>
          </v-card-text>

          <v-card-actions class="my-card-actions mt-4">
            <v-row>
              <v-col cols="12" sm>
                <v-btn
                  height="50px"
                  outlined
                  block
                  class="my-btn red-btn red-btn-sm"
                  @click="showDeleteConfirmation = false"
                >
                  {{$t("cancel")}}
                  <v-spacer></v-spacer>
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="12" sm>
                <v-btn
                  height="50px"
                  depressed
                  block
                  dark
                  @click.stop="deleteAudioInspection(audioInspectionUuid)"
                  class="my-btn save-btn save-btn-sm"
                >
                  {{$t("proceed")}}
                  <v-spacer></v-spacer>
                  <v-icon>mdi-check</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
  import { mapGetters, mapActions } from "vuex";
  import axios from "axios";

  import AudioInspectionDetailsCard from "../components/AudioInspections/AudioInspectionDetailsCard.vue";
  import AudioInspectionsModal from "../components/AudioInspections/AudioInspectionsModal.vue";
  import EditAudioInspectionModal from "../components/AudioInspections/EditAudioInspectionModal.vue";
  import NewAudioInspectionModal from "../components/AudioInspections/NewAudioInspectionModal.vue";

  export default {
    name: 'Inspections',
    components: {
      AudioInspectionDetailsCard,
      AudioInspectionsModal,
      EditAudioInspectionModal,
      NewAudioInspectionModal
    },
    computed: {
      ...mapGetters([
        "selectedApiary",
        "audioInspections",
        "inspectionDetails",
        "editActionItem",
        "completedTitle",
        "inventory",
        "user",
        "newAudioInspectionData",
        "editInspectionDetails",
        "newAudioUuid"
      ]),
      isChosenAudioInspectionEmpty() {
        return Object.keys(this.inspectionDetails).length == 0;
      }
    },
    data: () => ({
      showLoader: false,
      showEditAudioInspectionModal: false,
      showNewAudioInspectionModal: false,
      showDeleteConfirmation: false,
      checkbox_off: "$vuetify.icons.checkbox_off",
      delete_icon: "$vuetify.icons.delete_icon",
      download_icon: "$vuetify.icons.download_icon",
      publicPath: process.env.BASE_URL,
      headers: [
        {text: '', align: 'start', sortable: false, value: 'checkbox'},
        {text: '', align: 'start', sortable: false, value: 'title'},
        {text: '', align: 'end', sortable: false, value: 'download'},
        {text: '', align: 'end', sortable: false, value: 'delete'},
        {text: '', align: 'd-none', sortable: false, value: 'date'}
      ],
      chosenTask: {},
      audioInspectionUuid: ''
    }),
    mounted() {
      if (this.user && this.user.premium && this.user.premium == 1) {
        let apiaryId = this.selectedApiary.id;
        this.getAudioInspections(apiaryId);
        this.setIsHiddenApiaryButton(false);
      } else {
        this.$router.push("hives");
      }
    },
    methods: {
      ...mapActions([
        "getNewAudioInspectionUuid",
        "getAudioInspections",
        "toggleAudioInspectionActive",
        "setInspectionDetails",
        "setEditInspectionDetails",
        "setApiaryButton",
        "setIsHiddenApiaryButton",
        "resetNewAudioInspectionData"
      ]),
      selectAudioInspectionItem(item) {
        this.toggleAudioInspectionActive(item).then(() => {
          if (item.active) {
            this.setInspectionDetails(item);
          } else {
            this.setInspectionDetails({});
          }
        });
      },
      downloadAudioInspection(audioItem) {
        this.showLoader = true;
        let fileName = audioItem.file_name;
        
        axios({
          url: `${process.env.VUE_APP_BASE_URL}/api/recordings/${audioItem.uuid}/download`,
          method: 'GET',
          responseType: 'blob',
        }).then((response) => {
          const href = URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          this.showLoader = false;
        });
      },
      onDeleteAudioInspection(inspectionItem) {
        this.audioInspectionUuid = inspectionItem.uuid;
        this.showDeleteConfirmation = true;
      },
      deleteAudioInspection(audioInspectionUuid) {
        this.showLoader = true;
        axios({
          url: `${process.env.VUE_APP_BASE_URL}/api/recordings/${audioInspectionUuid}`,
          method: 'DELETE'
        }).then((response) => {
          this.showDeleteConfirmation = false;
          let apiaryId = this.selectedApiary.id;
          this.getAudioInspections(apiaryId).then(() => {
            this.showLoader = false;
          });
        });
      },
      openEditAudioInspectionModal() {
        let inspectionDetailsCopy = JSON.parse(JSON.stringify(this.inspectionDetails));
        this.setEditInspectionDetails(inspectionDetailsCopy).then(() => {
          this.setApiaryButton(true);
          this.showEditAudioInspectionModal = true;
          document.body.classList.add("modal-open");
        });        
      },
      closeAudioInspectionsModal() {
        this.setInspectionDetails({});
      },
      closeEditAudioInspectionModal() {
        this.setApiaryButton(false);
        this.showEditAudioInspectionModal = false;
        document.body.classList.remove("modal-open");
      },
      closeNewAudioInspectionModal() {
        this.resetNewAudioInspectionData();
        this.setApiaryButton(false);
        this.showNewAudioInspectionModal = false;
        document.body.classList.remove("modal-open");
      },
      updateAudioInspection() {
        this.showLoader = true;
        let url = `${process.env.VUE_APP_BASE_URL}/api/recordings/${this.editInspectionDetails.uuid}`;
        let requestData = {
          title: this.editInspectionDetails.title,
          notes: this.editInspectionDetails.notes,
          date: this.editInspectionDetails.date
        };

        axios.patch(url, requestData).then(response => {
          let apiaryId = this.selectedApiary.id;
          this.getAudioInspections(apiaryId).then(() => {
            this.showLoader = false;
            this.setInspectionDetails({});
            this.closeEditAudioInspectionModal();
          });
        }).catch(er => {
          console.log(er);
        });
      },
      saveNewInspection() {
        this.showLoader = true;
        let url = `${process.env.VUE_APP_BASE_URL}/api/recordings/${this.newAudioUuid}`;
        let requestData = {
          title: this.newAudioInspectionData.title,
          notes: this.newAudioInspectionData.notes,
          date: this.newAudioInspectionData.date
        };

        axios.patch(url, requestData).then(response => {
          let apiaryId = this.selectedApiary.id;
          this.getAudioInspections(apiaryId).then(() => {
            this.showLoader = false;
            this.showNewAudioInspectionModal = false;
            this.resetNewAudioInspectionData();
          });
        }).catch(er => {
          console.log(er);
        });
      },
      onAddInspection() {
        console.log('here')
        let payload = {
          apiaryId: this.selectedApiary.id
        }
        this.getNewAudioInspectionUuid(payload);
        this.showNewAudioInspectionModal = true;
      }
    }
  }
</script>

<style type="text/css">
  #hive-management-page .my-card {
    padding: 35px 40px;
  }

  #audio-inspections-table {
    margin-top: 12px;
  }

  #audio-inspections-table table td {
    padding: 15px 10px;
    border: none;
  }

  #audio-inspections-table table tr:hover {
    background: #EFF0F7;
  }

  #audio-inspections-table .v-data-footer {
    justify-content: flex-end;
    border: none;
    padding: 0;
  }

  #audio-inspections-table table td:first-child {
    width: 60px;
  }

  #audio-inspections-table table td:last-child {
    width: 0;
  }

  #audio-inspections-table table td:nth-child(3) {
    width: 30px;
    cursor: pointer;
  }

  .item-link {
    cursor: pointer;
  }
  
  #inspections-card .my-input {
    border-radius: 8px;
    font-weight: 600;
    font-size: 13px;
    line-height: 22px;
    letter-spacing: 0.25px;
    color: #14142B;
  }

  #inspections-card .my-input .v-input__slot::before,
  #inspections-card .my-input .v-input__slot::after {
    border:  none;
  }

  .task-title {
    font-weight: 600;
    font-size: 15px;
    line-height: 24px;
    letter-spacing: 0.75px;
  }

  .task-item-date {
    font-size: 12px;
    line-height: 22px;
    letter-spacing: 0.25px;
  }

  .file-icon {
    margin: auto 0 auto 16px;
  }

  .tasks-calendar-icon {
    margin: auto 10px auto 0;
  }

  .active-task {
    border-bottom: 3px solid #EAAC30;
  }

  div.active-task div.task-title {
    padding-bottom: 16px;
  }

  #my-app button.my-btn.add-inspection-btn {
    font-size: calc(15px + (20 - 15) * ((100vw - 375px) / (1440 - 375)));
    line-height: 32px;
    letter-spacing: 0.75px;
    background-color: #EAAC30;
    border: 2px solid #EAAC30;
    padding: 9px 22px;
    height: 60px;
  }

  #my-app button.my-btn.add-inspection-btn:hover {
    background-color: #ffffff;
    color: #262338;
  }

  #my-app button.add-inspection-btn:hover::before {
    opacity: 0;
  }

  @media screen and (max-width: 960px) {
    #hive-management-page .my-card {
      padding: 35px 25px;
    }

    #inspections-card.my-card {
      min-height: unset;
    }
  }
</style>
